// ✅ CRM Page - Dynamic Data Loading from Firebase

import { useState, useEffect } from "react";
import {
  ActionButton,
  FilterButton,
  Loader,
  SearchInput,
  TabButtonGroup,
} from "../components/shared";
import { buttonLabelCrm, customerActions } from "../utils/data";
import { useOutletContext, useNavigate } from "react-router-dom";
import { db } from "../utils/firebase/firebaseConfig";
import { collection, getDocs } from "firebase/firestore";

const Crm = () => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rightLoading, setRightLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const { toggleSidebar } = useOutletContext();
  const navigate = useNavigate();

  const handleCrmActionClick = (idx) => {
    if (idx === 0) navigate("/voice-calls");
    else if (idx === 1) navigate("/chat");
    else if (idx === 2) navigate("/email-inbox");
  };
  const filteredCustomers = customers.filter((customer) => {
    const query = searchQuery.toLowerCase();
    return (
      customer.name?.toLowerCase().includes(query) ||
      customer.email?.toLowerCase().includes(query)
    );
  });

  useEffect(() => {
    const fetchCustomers = async () => {
      const snapshot = await getDocs(collection(db, "crm_customers"));
      const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setCustomers(data);
      setSelectedCustomer(data[0]); // default to first customer
      setLoading(false);
    };
    fetchCustomers();
  }, []);
  if (loading) {
    return (
      <div className="w-full h-[calc(100vh-100px)] flex items-center justify-center">
        <Loader />
      </div>
    );
  }
  if (!selectedCustomer)
    return <div className="text-sm text-red-500">No customer data found.</div>;

  return (
    <div className="flex flex-row w-full min-h-[calc(100vh-100px)] pr-2 md:pr-5">
      <div className="flex flex-col w-full h-auto gap-2 lg:gap-2">
        <div className="w-full h-auto border-b border-[#0000002B] flex justify-between items-center pb-2">
          <h1 className="text-[13px] font-semibold leading-[100%] text-black font-inter">
            CRM / Customer Profile
          </h1>
          <div className="flex flex-row items-center gap-2 md:hidden">
            <button
              onClick={toggleSidebar}
              className="px-3 py-1 text-[10px] font-medium text-black border border-[#0000002B] rounded-md hover:bg-black-700 transition"
            >
              Menu
            </button>
          </div>
        </div>

        <div className="flex flex-row w-full gap-x-4">
          {/* Left Section */}
          <div className="flex flex-col w-full md:w-1/3 gap-y-2">
            <SearchInput
              placeholder="Search Customers .."
              width="w-full"
              value={searchQuery}
              onChange={setSearchQuery}
            />
            <div className="flex flex-row items-center justify-between w-full">
              <div className="flex flex-row items-center w-full gap-x-2">
                <FilterButton
                  label="Filter"
                  buttonClassName="shadow-[4px_4px_47.1px_0px_#00000040]"
                />
                <FilterButton
                  label="Sort"
                  icon="/icons/flowbite.png"
                  buttonClassName="shadow-[4px_4px_47.1px_0px_#00000040]"
                />
              </div>
              <div className="w-40">
                <ActionButton label="Add Customer" className="w-auto" />
              </div>
            </div>
            <div className="flex flex-col">
              {filteredCustomers.map((customer) => (
                <div
                  key={customer.customerId}
                  onClick={() => {
                    setRightLoading(true);
                    setTimeout(() => {
                      setSelectedCustomer(customer);
                      setRightLoading(false);
                    }, 300); // you can tweak duration
                  }}
                  className="flex cursor-pointer flex-col justify-between shadow-[4px_4px_47.1px_0px_#00000040] border-l border-r bg-white w-full gap-3 px-2 py-2 rounded-[12px] mt-2"
                  style={{ borderColor: customer.borderColor || "#00000020" }}
                >
                  <div className="flex flex-row items-start justify-between w-full">
                    <div className="flex flex-row">
                      <img
                        src={customer.profileImg}
                        alt="icon"
                        className="w-[50px] h-[50px] object-contain"
                      />
                      <div className="flex flex-col w-full pl-2 mt-1">
                        <span className="text-[12px] font-inter font-semibold text-black">
                          {customer.name}
                        </span>
                        <span className="text-[12px] font-inter text-[#00000073] text-opacity-80">
                          {customer.email}
                        </span>
                      </div>
                    </div>
                    <div
                      className="flex flex-row items-center gap-1 px-2 mt-1 rounded-lg"
                      style={{ backgroundColor: customer.badgeBg }}
                    >
                      <span
                        className="text-[10px] font-inter"
                        style={{ color: customer.badgeTextColor }}
                      >
                        {customer.badgeText}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-row items-center justify-between w-full">
                    <span className="text-[12px] font-inter text-[#00000073] text-opacity-80">
                      Last Contacted : {customer.lastContacted}
                    </span>
                    <span className="text-[12px] font-inter font-semibold text-black">
                      {customer.amount}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Section */}
          {/* ⬇ Inject dynamic customer data using selectedCustomer here */}
          {/* You can reuse your current right panel JSX and replace static values with selectedCustomer.gridItems, selectedCustomer.customerDetailCrm, etc. */}

          {/* Right Section */}
          <div className="flex-col hidden w-full md:flex md:w-2/3 gap-y-2">
            {rightLoading ? (
              <div className="flex items-center justify-center w-full h-full py-10">
                <Loader />
              </div>
            ) : (
              <>
                <div className="flex flex-col justify-between shadow-[4px_4px_47.1px_0px_#00000040] border-l border-r bg-white w-full gap-1 px-4 py-2 rounded-[12px] mt-2">
                  <div className="flex flex-row items-center justify-between w-full p-2">
                    <div className="flex flex-row">
                      <img
                        src={selectedCustomer.profileImg}
                        alt="icon"
                        className="w-[50px] h-[50px] object-contain"
                      />
                      <div className="flex flex-col w-full pl-2 mt-1">
                        <span className="text-[12px] font-inter font-semibold text-black">
                          {selectedCustomer.name}
                        </span>
                        <span className="text-[12px] font-inter text-[#00000073] text-opacity-80">
                          {selectedCustomer.email}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end justify-end w-full gap-y-2">
                      <div
                        className="flex flex-row w-auto gap-1 px-2 mt-1 rounded-lg"
                        style={{ backgroundColor: selectedCustomer.badgeBg }}
                      >
                        <span
                          className="text-[10px] font-inter"
                          style={{ color: selectedCustomer.badgeTextColor }}
                        >
                          {selectedCustomer.badgeText}
                        </span>
                      </div>
                      <div className="flex flex-row">
                        <div className="flex flex-row justify-end items-start gap-3 px-2 w-[144px] h-[41px]">
                          {customerActions.map((item, idx) => (
                            <div
                              key={idx}
                              className="w-[28px] h-[28px] cursor-pointer rounded-[5px] shadow-[4px_4px_20px_0px_#00000040] bg-white flex items-center justify-center"
                              onClick={() => handleCrmActionClick(idx)}
                            >
                              <img
                                src={item.icon}
                                alt={item.alt}
                                className="w-[18px] h-[18px]"
                              />
                            </div>
                          ))}
                        </div>
                        <ActionButton label="Edit Profile" hideIcon />
                      </div>
                    </div>
                  </div>

                  {/* Grid */}
                  <div className="flex flex-row items-center justify-between w-full px-2 gap-x-2">
                    {selectedCustomer.gridItems?.map((item, idx) => (
                      <div
                        key={idx}
                        className="flex flex-col gap-1 items-center justify-center w-full h-[60px] bg-[#ECECEC] rounded-[8px] shadow-sm"
                      >
                        <span className="text-[11px] font-inter text-black text-opacity-60">
                          {item.title}
                        </span>
                        <span className="text-[10px] font-inter text-black">
                          {item.subtext}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Tabs */}
                <div className="w-full px-2 mt-4">
                  <TabButtonGroup
                    buttonLabels={buttonLabelCrm}
                    selectedIndex={selectedIndex}
                    setSelectedIndex={setSelectedIndex}
                  />
                </div>

                {/* Details */}
                <div className="flex flex-col w-full gap-y-4">
                  <div className="flex flex-row justify-between mt-2 gap-x-4">
                    <div className="flex flex-col w-1/2 gap-4 p-5 bg-white rounded-[12px] shadow-[4px_4px_47.1px_0px_#00000040]">
                      {selectedCustomer.customerDetailCrm
                        ?.slice(0, 4)
                        .map((detail, idx) => (
                          <div
                            key={idx}
                            className="flex flex-row items-start gap-2"
                          >
                            <img
                              src={detail.icon}
                              alt={detail.label}
                              className="w-[18px] h-[18px] object-contain"
                            />
                            <div className="flex flex-col gap-y-1">
                              <span className="text-[12px] font-normal text-[#00000073] font-inter">
                                {detail.label}
                              </span>
                              {detail.value === "Active" ? (
                                <span className="text-[10px] font-medium font-inter px-2 py-[2px] rounded-full bg-[#E1FFD5] text-[#133F00] w-fit">
                                  Active
                                </span>
                              ) : (
                                <span className="text-[11px] font-medium text-[#000000CC] font-inter">
                                  {detail.value}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                    <div className="flex flex-col w-1/2 gap-4 p-5 bg-white rounded-[12px] shadow-[4px_4px_47.1px_0px_#00000040]">
                      {selectedCustomer.customerDetailCrm
                        ?.slice(4)
                        .map((detail, idx) => (
                          <div
                            key={idx}
                            className="flex flex-row items-start gap-2"
                          >
                            <img
                              src={detail.icon}
                              alt={detail.label}
                              className="w-[18px] h-[18px] object-contain"
                            />
                            <div className="flex flex-col gap-y-1">
                              <span className="text-[12px] font-normal text-[#00000073] font-inter">
                                {detail.label}
                              </span>
                              <span className="text-[11px] font-medium text-[#000000CC] font-inter">
                                {detail.value}
                              </span>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>

                  {/* Table */}
                  <div className="flex-1 w-full h-full">
                    <div className="w-full h-full shadow-[4px_4px_47.1px_0px_#00000040] rounded-[10px] px-1 pt-1 overflow-hidden">
                      <div className="flex flex-row justify-between w-full px-2 mt-2">
                        <div className="text-[11px] font-inter text-black">
                          Account Details
                        </div>
                        <div className="text-[11px] font-inter text-[#AA59F7]">
                          View All Orders
                        </div>
                      </div>
                      <div className="w-full h-[40px] border-b border-[#AAAAAA] px-2 flex items-center">
                        {["Order ID", "Date", "Items", "Total", "Status"].map(
                          (col, i) => (
                            <div
                              key={i}
                              className="text-[11px] px-1 font-normal text-black text-opacity-45 font-inter w-full"
                            >
                              {col}
                            </div>
                          )
                        )}
                      </div>
                      <div className="flex flex-col gap-0 overflow-y-auto max-h-[380px] 2xl:max-h-[450px] pr-1">
                        {selectedCustomer.tableDetailsCrm?.map((row, index) => (
                          <div
                            key={index}
                            className="w-full h-[50px] hover:bg-[#F6F9FF] rounded-[12px] flex items-center px-2"
                          >
                            <div className="w-full text-[10px] font-inter px-1">
                              {row.caller}
                            </div>
                            <div className="w-full text-[10px] font-inter px-1 text-black text-opacity-40">
                              {row.date}
                            </div>
                            <div className="w-full text-[10px] font-inter  px-1 text-black text-opacity-40">
                              {row.duration}
                            </div>
                            <div className="w-full px-1 text-[10px] font-inter text-black text-opacity-40">
                              {row.direction}
                            </div>
                            <div
                              className="w-full text-[10px] font-inter"
                              style={{ color: row.textColor }}
                            >
                              {row.status}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Crm;
